import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Key, CheckCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get("token") || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. Token may be invalid or expired.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="font-outfit text-3xl font-bold text-[#D4AF37]">Prestigious Wireless</Link>
          <p className="text-[#A0A0A0] mt-3 font-manrope">Set your new password</p>
        </div>

        {success ? (
          <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8 text-center" data-testid="reset-success">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-outfit text-xl font-bold text-[#F5F5F5] mb-2">Password Reset!</h2>
            <p className="text-[#A0A0A0] font-manrope text-sm mb-6">Your password has been updated successfully.</p>
            <Link to="/login" className="block w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3 rounded-full font-semibold transition-all" data-testid="go-to-login">
              Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8 space-y-6" data-testid="reset-password-form">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm font-manrope">{error}</div>}
            <div className="flex items-center gap-3 mb-2"><Key className="w-5 h-5 text-[#D4AF37]" /><p className="text-[#F5F5F5] font-manrope text-sm">Enter the reset code from your email and your new password.</p></div>
            <div>
              <label className="block text-[#A0A0A0] text-sm font-manrope mb-2">Reset Code</label>
              <input type="text" value={token} onChange={e => setToken(e.target.value)} required
                className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope font-mono outline-none transition-all"
                placeholder="Paste your reset token" data-testid="reset-token-input" />
            </div>
            <div>
              <label className="block text-[#A0A0A0] text-sm font-manrope mb-2">New Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope outline-none transition-all pr-12"
                  placeholder="Min 6 characters" data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3.5 rounded-full font-semibold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="reset-submit">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Resetting...</> : "Reset Password"}
            </button>
            <p className="text-center text-[#A0A0A0] text-sm font-manrope">
              Need a new code? <Link to="/forgot-password" className="text-[#D4AF37] font-semibold">Request again</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
